// Agent Service - API calls for agent chat functionality

import api from './api';
import type {
  AgentResponse,
  AgentDefinition,
  AgentUpdateRequest,
  ConversationSummary,
  Conversation,
  ToolInfo,
  ToolSetInfo,
  MCPServerInfo,
  ChatRequest,
} from '../types/agent';

// Format cost for display
export function formatCost(cost: number | undefined | null): string {
  if (cost === undefined || cost === null || cost === 0) return '$0.00';
  if (cost < 0.001) return `$${cost.toFixed(6)}`;
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(3)}`;
}

// Format latency for display
export function formatLatency(ms: number | undefined | null): string {
  if (ms === undefined || ms === null) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// Format tokens for display
export function formatTokens(tokens: number | undefined | null): string {
  if (tokens === undefined || tokens === null) return '0';
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`;
  return tokens.toString();
}

// Agent service object
export const agentService = {
  // Send a message to the agent
  async sendMessage(request: ChatRequest): Promise<AgentResponse> {
    const response = await api.post<AgentResponse>('/agent/chat', request, {
      timeout: 300000, // 5 minutes - LLM responses can take a while with tool calls
    });
    return response.data;
  },

  // List all conversations for the current user
  async listConversations(): Promise<ConversationSummary[]> {
    const response = await api.get<ConversationSummary[]>('/agent/conversations');
    return response.data;
  },

  // Get a specific conversation
  async getConversation(conversationId: string): Promise<Conversation> {
    const response = await api.get<Conversation>(`/agent/conversations/${conversationId}`);
    return response.data;
  },

  // Delete a conversation
  async deleteConversation(conversationId: string): Promise<void> {
    await api.delete(`/agent/conversations/${conversationId}`);
  },

  // List available agents
  async listAgents(): Promise<AgentDefinition[]> {
    const response = await api.get<AgentDefinition[]>('/agent/agents');
    return response.data;
  },

  // Update an existing agent
  async updateAgent(agentId: string, updates: AgentUpdateRequest): Promise<AgentDefinition> {
    const response = await api.put<AgentDefinition>(`/agent/agents/${agentId}`, updates);
    return response.data;
  },

  // List available tools from MCP servers
  async listTools(mcpServers?: string[], agentId?: string): Promise<ToolInfo[]> {
    const params: Record<string, string> = {};
    if (agentId) {
      params.agent_id = agentId;
    }
    if (mcpServers && mcpServers.length > 0) {
      params.mcp_servers = mcpServers.join(',');
    }
    const response = await api.get<{ tools: ToolInfo[]; total: number }>('/agent/tools', { params });
    return response.data.tools;
  },

  // List available tool sets from the MCP server
  async listToolSets(): Promise<ToolSetInfo[]> {
    const response = await api.get<{ tool_sets: ToolSetInfo[]; total: number }>('/agent/tool-sets');
    return response.data.tool_sets;
  },

  // List available MCP servers with their tools
  async listMCPServers(): Promise<MCPServerInfo[]> {
    const response = await api.get<{ servers: MCPServerInfo[]; total: number }>('/agent/mcp-servers');
    return response.data.servers;
  },
};

export default agentService;
